import { useState, useRef, useEffect, KeyboardEvent, useMemo } from 'react';
import styled from '@emotion/styled';
import { theme } from '../styles/theme';
import { useT } from '../i18n';
import type { FilterOption } from '../types';

// Mirrors the dropdown-labeled / .dropdown structure used in CascadingSelect
// — see that file's leading comments for the rationale.
const Container = styled.div<{ isOpen: boolean }>`
  position: relative;
  display: inline-flex;
  flex-direction: column;
  gap: var(--space-1);
  width: 100%;
  z-index: ${props => props.isOpen ? 100 : 10};
`;

const Label = styled.label<{ disabled?: boolean }>`
  font-family: var(--sans-stack);
  font-size: 1rem;
  line-height: 1.2;
  color: ${props => props.disabled ? 'var(--color-text-muted)' : 'var(--color-text-default)'};
  padding: 0 0.8em;
  opacity: ${props => props.disabled ? 0.6 : 1};
`;

const SelectButton = styled.div<{ isOpen: boolean; hasValue: boolean; disabled?: boolean }>`
  box-sizing: border-box;
  font-family: var(--sans-stack);
  font-size: 1rem;
  line-height: 1.2;
  padding: 0.3em 2.6em 0.2em 0.8em;
  border-radius: 999px;
  border: 1px solid ${props => props.isOpen ? 'var(--color-text-default)' : 'rgba(0, 0, 0, 0.1)'};
  background-color: ${props => props.disabled ? '#f5f5f5' : 'transparent'};
  background-image: url("data:image/svg+xml;charset=utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' height='24' viewBox='0 -960 960 960' width='24' fill='%235f6368'%3E%3Cpath d='M480-360 280-560h400L480-360Z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.1em center;
  background-size: 2em;
  color: ${props => props.disabled ? 'var(--color-text-muted)' : props.hasValue ? 'var(--color-text-default)' : 'var(--color-text-muted)'};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  outline: none;
  width: 100%;
  text-align: left;
  position: relative;
  transition: border-color 0.2s, background-color 0.2s;
  opacity: ${props => props.disabled ? 0.6 : 1};

  &:focus-visible {
    outline: 0;
    box-shadow: ${props => props.disabled ? 'none' : '0 0 0 3px var(--color-focus-ring)'};
  }

  &:hover {
    background-color: ${props => props.disabled ? '#f5f5f5' : 'rgba(0, 0, 0, 0.03)'};
  }
`;

// Panel matches website screenshot: clean white card, subtle shadow, 8px
// radius, no border. Open/close transition kept (visibility cross-fade).
const Dropdown = styled.div<{ isOpen: boolean }>`
  box-sizing: border-box;
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  min-width: 100%;
  background: #ffffff;
  border-radius: var(--radius-m);
  box-shadow: var(--shadow-m);
  z-index: 1000;
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transform: ${props => props.isOpen ? 'translateY(0) scale(1)' : 'translateY(-8px) scale(0.98)'};
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
`;

const SearchInputWrapper = styled.div`
  padding: var(--space-3) var(--space-4);
  position: relative;
`;

// Magnifier on the RIGHT (inside the pill) — matches website convention.
const SearchInputIcon = styled.span`
  position: absolute;
  right: calc(var(--space-4) + 0.8em);
  top: 50%;
  transform: translateY(-50%);
  color: var(--color-text-muted);
  display: flex;
  align-items: center;
  pointer-events: none;
`;

const SearchInput = styled.input`
  box-sizing: border-box;
  font-family: var(--sans-stack);
  font-size: 1rem;
  padding: 0.5em 2.4em 0.5em 0.8em;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 999px;
  background: transparent;
  color: var(--color-text-default);
  outline: none;
  width: 100%;

  &::placeholder { color: var(--color-text-muted); }
  &:focus {
    border-color: var(--color-text-default);
  }
`;

const OptionsList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 320px;
  overflow-y: auto;

  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: var(--color-bg-muted); border-radius: 3px; }
`;

const OptionItem = styled.li<{ isHighlighted: boolean; isSelected: boolean }>`
  box-sizing: border-box;
  padding: var(--space-3) var(--space-4);
  cursor: pointer;
  font-family: var(--sans-stack);
  font-size: 1rem;
  line-height: 1.3;
  color: var(--color-text-default);
  border-bottom: 1px solid var(--color-bg-muted);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--space-3);

  &:last-child { border-bottom: none; }
  &:hover { background: var(--color-bg-surface); }
  ${props => props.isHighlighted && 'background: var(--color-bg-surface);'}
`;

const OptionLabel = styled.span`
  flex: 1;
  line-height: 1.3;
  word-break: break-word;
  min-width: 0;
`;

const OptionCount = styled.span`
  color: var(--color-text-muted);
  margin-left: 0.4em;
`;

const NoResults = styled.div`
  padding: var(--space-6);
  text-align: center;
  color: var(--color-text-muted);
  font-family: var(--sans-stack);
  font-size: 1rem;
`;

const ResultsCount = styled.div`
  padding: var(--space-2) var(--space-4);
  font-family: var(--sans-stack);
  font-size: 1rem;
  color: var(--color-text-muted);
  border-bottom: 1px solid var(--color-bg-muted);
`;

const ClearButton = styled.div`
  position: absolute;
  right: 32px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  font-size: 13px;
  border: none;
  background: ${theme.colors.background};
  border-radius: 50%;
  cursor: pointer;
  color: ${theme.colors.text.secondary};
  transition: all 0.15s;
  padding: 0;

  &:hover {
    background: ${theme.colors.error};
    color: white;
    transform: translateY(-50%) scale(1.1);
  }
`;

const DisplayText = styled.span`
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding-right: 24px;
  font-weight: 450;
`;

const SearchIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

interface SearchableSelectProps {
  options: FilterOption[];
  value: string | null;
  onChange: (value: string | null) => void;
  placeholder: string;
  label: string;
  formatOption?: (opt: FilterOption) => string;
  disabled?: boolean;
  searchable?: boolean;
  clearable?: boolean;
  showCount?: boolean;
  // Fires whenever the dropdown opens/closes. The mobile filter sheet uses this
  // to hide its sticky footer while a dropdown is open — see FilterPanel.
  onOpenChange?: (open: boolean) => void;
}

export function SearchableSelect({
  options,
  value,
  onChange,
  placeholder,
  label,
  formatOption,
  disabled = false,
  searchable = true,
  clearable = true,
  showCount = true,
  onOpenChange,
}: SearchableSelectProps) {
  const m = useT();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;
    const lowerSearch = searchTerm.toLowerCase();
    return options.filter(opt => {
      // Search in multiple fields for better matching
      const displayText = formatOption ? formatOption(opt) : opt.label;
      // Include searchTerms if available (for merged chapters with historical names)
      const searchTermsText = (opt as any).searchTerms || '';
      const searchableText = `${opt.value} ${opt.label} ${displayText} ${searchTermsText}`.toLowerCase();
      return searchableText.includes(lowerSearch);
    });
  }, [options, searchTerm, formatOption]);

  const selectedOption = useMemo(() => {
    return options.find(opt => opt.value === value) || null;
  }, [options, value]);

  const displayValue = useMemo(() => {
    if (!selectedOption) return placeholder;
    return formatOption ? formatOption(selectedOption) : selectedOption.label;
  }, [selectedOption, placeholder, formatOption]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Report open/close to the parent. The cleanup fires the matching `false`
  // when isOpen flips back or the component unmounts, so open/close stay
  // balanced even if the menu is mounted while open.
  useEffect(() => {
    if (!isOpen) return;
    onOpenChange?.(true);
    return () => onOpenChange?.(false);
  }, [isOpen, onOpenChange]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      // Small delay to ensure dropdown is visible
      setTimeout(() => searchInputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  // Reset highlighted index when filtered options change
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [filteredOptions]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const items = listRef.current.querySelectorAll('li');
      const item = items[highlightedIndex];
      if (item) {
        item.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex]);

  const handleKeyDown = (e: KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else {
          setHighlightedIndex(prev =>
            prev < filteredOptions.length - 1 ? prev + 1 : prev
          );
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => (prev > 0 ? prev - 1 : 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
          handleSelect(filteredOptions[highlightedIndex].value);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSearchTerm('');
        break;
    }
  };

  const handleSelect = (optValue: string) => {
    onChange(optValue);
    setIsOpen(false);
    setSearchTerm('');
    setHighlightedIndex(-1);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
  };

  const toggleDropdown = () => {
    if (disabled) return;
    setIsOpen(!isOpen);
    if (isOpen) {
      setSearchTerm('');
    }
  };

  return (
    <Container ref={containerRef} isOpen={isOpen} data-filter="dropdown">
      <Label disabled={disabled} data-filter="dropdown-label">{label}</Label>
      <SelectButton
        isOpen={isOpen}
        hasValue={!!value}
        disabled={disabled}
        onClick={toggleDropdown}
        onKeyDown={handleKeyDown}
        data-filter="dropdown-button"
      >
        <DisplayText data-filter="dropdown-value">{displayValue}</DisplayText>
        {clearable && value && (
          <ClearButton onClick={handleClear} title={m.common.reset} data-filter="dropdown-clear">
            <span className="icon" data-icon="close" />
          </ClearButton>
        )}
        {/* Chevron is rendered via SelectButton's background-image — see styled definition above. */}
      </SelectButton>

      <Dropdown isOpen={isOpen} data-filter="dropdown-menu">
        {searchable && (
          <SearchInputWrapper data-filter="dropdown-search-wrapper">
            <SearchInputIcon>
              <SearchIcon />
            </SearchInputIcon>
            <SearchInput
              ref={searchInputRef}
              type="text"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={m.common.search}
              data-filter="dropdown-input"
            />
          </SearchInputWrapper>
        )}
        {searchable && searchTerm && (
          <ResultsCount data-filter="dropdown-results-count">
            {m.select.resultsCount(filteredOptions.length, options.length)}
          </ResultsCount>
        )}
        {filteredOptions.length > 0 ? (
          <OptionsList ref={listRef} key={`list-${searchTerm}-${filteredOptions.length}`} data-filter="dropdown-options">
            {filteredOptions.map((opt, index) => (
              <OptionItem
                key={opt.value}
                isHighlighted={index === highlightedIndex}
                isSelected={opt.value === value}
                onClick={() => handleSelect(opt.value)}
                onMouseEnter={() => setHighlightedIndex(index)}
                data-filter="dropdown-option"
              >
                <OptionLabel data-filter="dropdown-option-label">
                  {formatOption ? formatOption(opt) : opt.label}
                  {showCount && <OptionCount data-filter="dropdown-option-count">({opt.count})</OptionCount>}
                </OptionLabel>
              </OptionItem>
            ))}
          </OptionsList>
        ) : (
          <NoResults data-filter="dropdown-no-results">{m.select.noResultsFor(searchTerm)}</NoResults>
        )}
      </Dropdown>
    </Container>
  );
}
